import { Check, ChevronDown, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui";
import { useEvidenceUserSearch } from "~/features/evidences/hooks";
import { cn } from "~/lib/utils";

interface EvidenceUserSelectProps {
  selectedId?: string;
  onChange: (id: string | undefined) => void;
}

/** Single-select async user filter (debounced search inside a popover). */
export function EvidenceUserSelect({ selectedId, onChange }: EvidenceUserSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  const { options, loading, search, reset, resolveUser } = useEvidenceUserSearch();

  // Resolve the label of a pre-selected user (e.g. restored from a shared link).
  const resolvedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedId) {
      setSelectedLabel(null);
      resolvedFor.current = null;
      return;
    }
    if (resolvedFor.current === selectedId) return;
    resolvedFor.current = selectedId;
    void resolveUser(selectedId).then((user) => setSelectedLabel(user?.displayName ?? selectedId));
  }, [selectedId, resolveUser]);

  const triggerLabel = selectedId ? (selectedLabel ?? selectedId) : t("ui.filterByX", { x: t("model.user.modelName") });

  return (
    <div className="flex items-center gap-1">
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) reset();
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn("min-w-0 flex-1 justify-between", selectedId && "border-primary-500 text-primary-700")}
          >
            <span className="truncate">{triggerLabel}</span>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0">
          <Command shouldFilter={false}>
            <CommandInput placeholder={t("ui.search")} onValueChange={search} />
            <CommandList>
              {!loading && <CommandEmpty>{t("feature.common.noData")}</CommandEmpty>}
              {options.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.id}
                  onSelect={() => {
                    onChange(option.id);
                    setSelectedLabel(option.displayName);
                    resolvedFor.current = option.id;
                    setOpen(false);
                  }}
                >
                  <Check className={cn("h-4 w-4", selectedId === option.id ? "opacity-100" : "opacity-0")} />
                  <span className="truncate text-fg-1">{option.displayName}</span>
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selectedId && (
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => onChange(undefined)}
          aria-label={t("ui.clearAll")}
        >
          <X />
        </Button>
      )}
    </div>
  );
}
